import { CSSProperties } from 'react';
import { AllSpritesheets } from '../../data/spritesheets/generated';
import { FacesSpritesheets } from '../../data/spritesheets/faces.config';
import './Icon.scss';

interface FrameEntry {
  frame: { x: number; y: number; w: number; h: number };
  image: string;
  atlasW: number;
  atlasH: number;
}

// Merge every spritesheet (icon atlases AND entity sheets) into a single
// frame-name → frame+image lookup, so a sprite name resolves regardless of
// which sheet (and image) it lives in — lets the bestiary reuse Icon to render
// a monster idle frame, not just UI icons.
const FRAME_LOOKUP: Record<string, FrameEntry> = {};
for (const sheet of [...AllSpritesheets, ...FacesSpritesheets]) {
  const m = sheet.metadata;
  const image = m.meta.image as string;
  const atlasW = m.meta.size?.w ?? 1024;
  const atlasH = m.meta.size?.h ?? 1024;
  const frames = m.frames as Record<
    string,
    { frame: { x: number; y: number; w: number; h: number } }
  >;
  for (const [name, data] of Object.entries(frames)) {
    FRAME_LOOKUP[name] = { frame: data.frame, image, atlasW, atlasH };
  }
}

/** True when `name` resolves to a frame in any spritesheet. */
export function frameExists(name: string): boolean {
  return name in FRAME_LOOKUP;
}

interface Props {
  sprite?: string;
  size?: number;
  /** Fill 100% of the parent (width & height) instead of a fixed `size`. */
  fill?: boolean;
  /** Icon frame rendered bottom-right at 1/5 the size (no-op if unresolved). */
  overlay?: string;
  className?: string;
}

export const Icon = ({
  sprite,
  size = 40,
  fill = false,
  overlay,
  className,
}: Props) => {
  const entry = sprite ? FRAME_LOOKUP[sprite] : undefined;
  const overlayResolved = overlay ? FRAME_LOOKUP[overlay] : undefined;

  const overlayNode = overlayResolved ? (
    <div className="icon__overlay">
      <Icon sprite={overlay} fill />
    </div>
  ) : null;

  if (!entry) {
    const placeholderStyle: CSSProperties = fill
      ? { width: '100%', height: '100%', fontSize: '1.5rem' }
      : { width: size, height: size, fontSize: size * 0.6 };
    return (
      <div
        className={`icon icon--placeholder${className ? ` ${className}` : ''}`}
        style={placeholderStyle}
      >
        ?
      </div>
    );
  }

  const { frame, image, atlasW, atlasH } = entry;
  let style: CSSProperties;
  if (fill) {
    // Stretch the frame to fill the parent (percentage size/position, so it is
    // independent of the parent's pixel size).
    style = {
      width: '100%',
      height: '100%',
      backgroundImage: `url('${image}')`,
      backgroundSize: `${(atlasW / frame.w) * 100}% ${(atlasH / frame.h) * 100}%`,
      backgroundPosition: `${(frame.x / (atlasW - frame.w)) * 100}% ${
        (frame.y / (atlasH - frame.h)) * 100
      }%`,
      imageRendering: 'pixelated',
    };
  } else {
    const scale = size / frame.w;
    style = {
      width: size,
      height: size,
      backgroundImage: `url('${image}')`,
      backgroundPosition: `-${frame.x * scale}px -${frame.y * scale}px`,
      backgroundSize: `${atlasW * scale}px ${atlasH * scale}px`,
      imageRendering: 'pixelated',
    };
  }

  return (
    <div className={`icon${className ? ` ${className}` : ''}`} style={style}>
      {overlayNode}
    </div>
  );
};
